import {Component, Inject, Optional} from "@angular/core";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {
    AddUpdateReminderRequest, AiReminderMinutesToOptionMap,
    reminderOptions,
    ReminderResponse,
    ReminderStatus
} from "../../Models/dashboard.model";
import {format} from "date-fns";
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {Subject} from "rxjs";
import {DashboardService} from "../../Services/dashboard.service";
import {SharedService} from "../../../../services/shared.service";
import {takeUntil} from "rxjs/operators";
import {HandleErrorService} from "../../../../services/handle-error-service";
import {MeetingFormGroupFieldsConstants} from "../../../../modules/meetings-workflow/models/meeting.model";
import {convertTimeAMPMToHHMMSS, convertToHHMM, convertToHHMMSS} from "../../../../UtilFunctions/global.utils";
import {ToastrService} from "ngx-toastr";
import {REMINDER_VALIDATION_CONSTANTS} from "../../../../form-validation.constants";

import {CommonModule, NgIf} from "@angular/common";
import {NgxMaterialTimepickerModule} from "ngx-material-timepicker";
import {MatSelectModule} from "@angular/material/select";
import {MatInputModule} from "@angular/material/input";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatButtonModule} from "@angular/material/button";
import {CustomDropdownOption} from "../../../../modules/shared/Models/shared-data-models.model";
import {AiDataTransformerService} from "../../../Shared/voice-instructions/Services/ai-data-transformer.service";
import {AiInsDetectionResponse} from "../../../Shared/voice-instructions/Models/ai-model.model";

@Component({
    selector: "app-add-edit-reminder",
    templateUrl: "./add-edit-reminder.component.html",
    styleUrls: ["./add-edit-reminder.component.scss"],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        NgIf,
        NgxMaterialTimepickerModule,
        MatDialogModule,
        MatSelectModule,
        MatInputModule,
        MatDatepickerModule,
        MatButtonModule,
    ]
})
export class AddEditReminderComponent {
    reminderForm: FormGroup;
    isModifying: boolean = false;
    isEditMode: boolean = false;
    // Getters for form controls
    minReminderTitleLength: number = REMINDER_VALIDATION_CONSTANTS.REMINDER_TITLE_MIN_LENGTH;
    maxReminderTitleLength: number = REMINDER_VALIDATION_CONSTANTS.REMINDER_TITLE_MAX_LENGTH;
    minDescriptionLength: number = REMINDER_VALIDATION_CONSTANTS.REMINDER_DESC_MIN_LENGTH;
    maxDescriptionLength: number = REMINDER_VALIDATION_CONSTANTS.REMINDER_DESC_MAX_LENGTH;
    reminderStatus: ReminderStatus;
    reminderList: CustomDropdownOption[];
    minDate: Date;
    protected readonly MeetingFormGroupFieldsConstants = MeetingFormGroupFieldsConstants;
    protected readonly ReminderStatus = ReminderStatus;
    private destroy$ = new Subject<void>();

    constructor(
        private dashboardService: DashboardService,
        private sharedService: SharedService,
        @Optional() @Inject(MAT_DIALOG_DATA) private data: ReminderResponse | null,
        private dialogRef: MatDialogRef<AddEditReminderComponent>,
        private handleErrorService: HandleErrorService,
        private toastrService: ToastrService,
        private aiDataTransformerService: AiDataTransformerService,
    ) {
        this.minDate = new Date();
        this.reminderList = reminderOptions;
    }

    get reminderTitle() {
        return this.reminderForm.get("reminderTitle");
    }

    get description() {
        return this.reminderForm.get("description");
    }

    get reminderDate() {
        return this.reminderForm.get("reminderDate");
    }

    get reminderTime() {
        return this.reminderForm.get("reminderTime");
    }

    get earlyReminderTime() {
        return this.reminderForm.get("earlyReminderTime");
    }

    notifyDateToTime(earlyReminderTime: string | null, reminderDate: string, reminderTime: string): string | null {
        if(earlyReminderTime == null){
            return null;
        }
        const reminderDateTime = new Date(`${reminderDate}T${reminderTime}`);
        const notificationDateTime = new Date(earlyReminderTime);

        const notificationMilliSeconds = reminderDateTime.getTime() - notificationDateTime.getTime();

        const notificationMinutes = notificationMilliSeconds / (1000 * 60);

        let notifyBefore:string | null =null;

        switch (notificationMinutes) {
            case 1440:
                notifyBefore = "1d";
                break;
            case 60:
                notifyBefore = "1h";
                break;
            case 30:
                notifyBefore = "30m";
                break;
            case 15:
                notifyBefore = "15m";
                break;
            case 10:
                notifyBefore = "10m";
                break;
            case 5:
                notifyBefore = "5m";
                break;
            case 0:
                notifyBefore = "atEvent";
                break;
            default: break;
        }

        return notifyBefore;
    }

    ngOnInit() {
        this.initializeForm();
        if (this.data) {
            this.isModifying = true;
            this.isEditMode = false;
            this.reminderStatus = this.data.reminderStatus;
            this.reminderForm.patchValue({
                reminderTitle: this.data.reminderTitle,
                description: this.data.description,
                reminderDate: this.data.reminderDate,
                reminderTime: this.data.reminderTime ? convertToHHMM(this.data.reminderTime) : "00:00",
                earlyReminderTime: this.notifyDateToTime(this.data.earlyReminderTime, this.data.reminderDate, this.data.reminderTime),

            });
            this.reminderForm.updateValueAndValidity();
            this.reminderDate?.updateValueAndValidity();
            this.reminderTime?.disable();
            this.reminderTime?.updateValueAndValidity();

            this.earlyReminderTime?.disable()
            // this.earlyReminderTime?.updateValueAndValidity();
        } else {
            this.isEditMode = true;
        }
        const aiResponse = this.aiDataTransformerService.getReminderDataValue() as AiInsDetectionResponse | null;
        if (aiResponse) {
            this.aiDataTransformerService.setReminderData(null); // consume once
            if (this.data && !this.isEditMode) {
                this.toggleEditMode(); // AI update flow starts editable
            }
            this.prefillFromAiResponse(aiResponse);
        }
    }

    /** Prefill only — validators still gate the save. */
    private prefillFromAiResponse(aiResponse: AiInsDetectionResponse) {
        if (aiResponse.title) {
            this.reminderTitle?.setValue(aiResponse.title.trim().slice(0, this.maxReminderTitleLength));
        }
        if (aiResponse.description) {
            this.description?.setValue(aiResponse.description.trim().slice(0, this.maxDescriptionLength));
        }
        const reminderDateTime = aiResponse.expectedStartDateAndTime
            ? this.parseAiDate(aiResponse.expectedStartDateAndTime)
            : null;
        if (reminderDateTime) {
            console.log("reminderDateTime", reminderDateTime);
            console.log("DateNow", Date.now());
            if (reminderDateTime.getTime() >= Date.now()) {
                this.reminderDate?.setValue(reminderDateTime);
                if (this.reminderTime?.enabled) {
                    this.reminderTime?.setValue(format(reminderDateTime, "HH:mm"));
                }
            } else {
                this.toastrService.info("The requested reminder date is in the past — please pick a new date.");
            }
        }
        if (aiResponse.reminderTime != null && this.earlyReminderTime?.enabled) {
            const option = AiReminderMinutesToOptionMap[aiResponse.reminderTime];
            if (option) {
                this.earlyReminderTime?.setValue(option);
            }
        }
    }

    /** AI dates are dd-MM-yyyyTHH:mm:ss; tolerate yyyy-MM-dd too. */
    private parseAiDate(dateStr: string): Date | null {
        const [datePart, timePart] = dateStr.split("T");
        const parts = datePart?.split("-");
        if (!parts || parts.length !== 3) return null;
        const isYearFirst = parts[0].length === 4;
        const [year, month, day] = isYearFirst ? parts : [parts[2], parts[1], parts[0]];
        const result = new Date(`${year}-${month}-${day}T${timePart || "00:00:00"}`);
        return isNaN(result.getTime()) ? null : result;
    }

    initializeForm() {
        this.reminderForm = new FormGroup({
            reminderTitle: new FormControl(null, [
                Validators.required,
                Validators.minLength(this.minReminderTitleLength),
                Validators.maxLength(this.maxReminderTitleLength),
            ]),
            description: new FormControl(null, [
                Validators.minLength(this.minDescriptionLength),
                Validators.maxLength(this.maxDescriptionLength),
            ]),
            reminderDate: new FormControl(null, [Validators.required]),
            reminderTime: new FormControl(null, [Validators.required]),
            earlyReminderTime: new FormControl('30m')
        });
    }

    toggleEditMode() {
        this.isEditMode = !this.isEditMode;
        if (this.isEditMode) {
            this.reminderTime?.enable();
        } else {
            this.reminderTime?.disable();
        }
    }

    formValid() {
        this.reminderTitle?.value ? this.reminderTitle?.setValue(this.reminderTitle?.value.trim()) : "";
        this.description?.value ? this.description?.setValue(this.description?.value.trim()) : "";
        this.reminderForm.updateValueAndValidity();
        return this.reminderForm.valid;
    }

    markAsComplete() {
        if (this.data) {
            this.sharedService.showSpinner();
            this.dashboardService
                .markReminderAsDone(this.data, this.sharedService.getAllAccountIdsOfLoggedInUser() as string)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (response) => {
                        this.sharedService.hideSpinner();
                        this.toastrService.success("Reminder Marked as Done Successfully", "Success");
                        this.dialogRef.close(response.data);
                    },
                    error: (error) => {
                        this.handleErrorService.handleError(error);
                    },
                });
        }
    }

    onSubmit() {
        if (this.formValid()) {
            // const formData = this.reminderForm.value;
            const formData = this.reminderForm.getRawValue();
            let addReminderRequest = new AddUpdateReminderRequest();
            addReminderRequest.reminderTitle = formData.reminderTitle;
            addReminderRequest.description = formData.description ?? "";
            addReminderRequest.reminderDate = format(new Date(formData.reminderDate), "yyyy-MM-dd");
            addReminderRequest.reminderTime =
                convertToHHMMSS(convertTimeAMPMToHHMMSS(formData.reminderTime)) || "00:00:00";
            addReminderRequest.reminderStatus = this.isModifying ? this.reminderStatus : ReminderStatus.PENDING;

            addReminderRequest.earlyReminderTime = this.computeNotifyTime(addReminderRequest.reminderDate, addReminderRequest.reminderTime, formData.earlyReminderTime);

            if(formData.earlyReminderTime === null){
                addReminderRequest.isEarlyReminderSet = false;
            }else{
                addReminderRequest.isEarlyReminderSet = true;
            }
            if (!this.isModifying) {
                let accountIds = (this.sharedService.getAllAccountIdsOfLoggedInUser(false) as number[])
                    ? (this.sharedService.getAllAccountIdsOfLoggedInUser(false) as number[])
                    : [];
                addReminderRequest.accountIdCreator = accountIds[0];
                this.createReminder(addReminderRequest);
            } else {
                this.modifyReminder(addReminderRequest);
            }
        } else {
            this.reminderForm.markAllAsTouched();
        }
    }

    computeNotifyTime(reminderDate: string, reminderTime: string, earlyReminderTime: string){
        const reminderDateTime = new Date(`${reminderDate}T${reminderTime}`);

        let notifyMilliseconds = 0;

        if(earlyReminderTime === null ||!earlyReminderTime && this.isModifying ){
            return null;
        }

        const value = parseInt(earlyReminderTime);
        const unit = earlyReminderTime.replace(value.toString(), "");

        switch (unit){
            case 'atEvent':
                notifyMilliseconds = 0;
                break
            case 'm':
                notifyMilliseconds = value * 60 * 1000;
                break;
            case 'h':
                notifyMilliseconds = value * 60 * 60 * 1000;
                break;
            case 'd':
                notifyMilliseconds = value * 24 * 60 * 60 *1000;
                break;
            default:
                notifyMilliseconds = 0;
        }

        return this.toLocalISOString(new Date(reminderDateTime.getTime() - notifyMilliseconds));

    }

     toLocalISOString(date: Date) {
        const pad = (num:any) => num.toString().padStart(2, '0');
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private createReminder(addReminderRequest: AddUpdateReminderRequest) {
        this.sharedService.showSpinner();
        this.dashboardService
            .addReminder(addReminderRequest, this.sharedService.getAllAccountIdsOfLoggedInUser() as string)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.sharedService.hideSpinner();
                    this.toastrService.success("Reminder Created Successfully", "Success");
                    this.dialogRef.close(response.data);
                },
                error: (error) => {
                    this.sharedService.hideSpinner();
                    this.handleErrorService.handleError(error);
                },
            });
    }

    private modifyReminder(addReminderRequest: AddUpdateReminderRequest) {
        if (this.data && this.data.reminderId) {
            addReminderRequest.accountIdCreator = this.data.userDetails.accountId;
            this.sharedService.showSpinner();
            this.dashboardService
                .updateReminder(
                    this.data.reminderId,
                    addReminderRequest,
                    this.sharedService.getAllAccountIdsOfLoggedInUser() as string,
                )
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (response) => {
                        this.sharedService.hideSpinner();
                        this.toastrService.success("Reminder Updated Successfully", "Success");
                        this.dialogRef.close(response.data);
                    },
                    error: (error) => {
                        this.sharedService.hideSpinner();
                        this.handleErrorService.handleError(error);
                    },
                });
        }
    }

    protected readonly REMINDER_VALIDATION_CONSTANTS = REMINDER_VALIDATION_CONSTANTS;
}
